import { create } from 'zustand';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';

export type ZoomAccessLevel = 'reader' | 'writer';

/**
 * Guided Zoom-setup state, persisted SERVER-SIDE (per user) so it survives
 * reloads, cache clears, and follows the user across devices. The store holds a
 * local cache for synchronous reads; writes are optimistic + written through to
 * the backend (PUT /connection/setup-state).
 */
interface SetupState {
  setupConversationId: string | null;
  accessLevel: ZoomAccessLevel | null;
  /** Derived server-side: the tenant has a usable read credential. */
  connected: boolean;
  /** True once the initial server load has completed. */
  loaded: boolean;
  /** Ephemeral: true while ZAC is waiting for the user to type the S2S app
   *  name in chat (the create-app "Name your app" step). Not persisted. */
  awaitingAppName: boolean;
  /** Ephemeral mid-flow progress for the setup stepper. Not persisted — on
   *  reload the durable signals (connected / granted scopes) take over. */
  appCreated: boolean;
  credsCaptured: boolean;
  infoFilled: boolean;
  /** User confirmed the manual add-scopes step (or the driven step ran). */
  scopesConfirmed: boolean;
  activated: boolean;

  loadSetupState: () => Promise<void>;
  setSetupConversationId: (id: string | null) => void;
  setAccessLevel: (level: ZoomAccessLevel | null) => void;
  setAwaitingAppName: (awaiting: boolean) => void;
  setAppCreated: (created: boolean) => void;
  setCredsCaptured: (captured: boolean) => void;
  setInfoFilled: (filled: boolean) => void;
  setScopesConfirmed: (confirmed: boolean) => void;
  setActivated: (activated: boolean) => void;
}

interface SetupStateResponse {
  accessLevel: ZoomAccessLevel | null;
  conversationId: string | null;
  connected: boolean;
}

async function persist(body: Record<string, unknown>): Promise<void> {
  try {
    const token = getToken();
    await fetch(`${API_BASE}/connection/setup-state`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        'X-Tenant-ID': '',
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Non-fatal: the optimistic local value still drives the UI this session.
  }
}

export const useSetupStore = create<SetupState>((set) => ({
  setupConversationId: null,
  accessLevel: null,
  connected: false,
  loaded: false,
  awaitingAppName: false,
  appCreated: false,
  credsCaptured: false,
  infoFilled: false,
  scopesConfirmed: false,
  activated: false,

  loadSetupState: async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/setup-state`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!res.ok) {
        set({ loaded: true });
        return;
      }
      const data = (await res.json()) as SetupStateResponse;
      set({
        setupConversationId: data.conversationId,
        accessLevel: data.accessLevel,
        connected: data.connected,
        loaded: true,
      });
    } catch {
      set({ loaded: true });
    }
  },

  setSetupConversationId: (setupConversationId) => {
    set({ setupConversationId });
    void persist({ conversationId: setupConversationId });
  },

  setAccessLevel: (accessLevel) => {
    set({ accessLevel });
    void persist({ accessLevel });
  },

  setAwaitingAppName: (awaitingAppName) => set({ awaitingAppName }),

  setAppCreated: (appCreated) => set({ appCreated }),

  setCredsCaptured: (credsCaptured) => set({ credsCaptured }),

  setInfoFilled: (infoFilled) => set({ infoFilled }),

  setScopesConfirmed: (scopesConfirmed) => set({ scopesConfirmed }),

  setActivated: (activated) => set({ activated }),
}));
